import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUpcomingEvents } from '@/services/events.api';
import type { FrontendEvent } from '@/services/transform';
import EventDetailModal from '@/pages/events/components/EventDetailModal';

const TYPE_COLORS: Record<string, string> = {
  offline: 'bg-emerald-100 text-emerald-700',
  online: 'bg-sky-100 text-sky-700',
  hybrid: 'bg-amber-100 text-amber-700',
};

const MONTH_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function formatDate(dateStr: string, endDate: string, lang: string): string {
  const start = parseLocalDate(dateStr);
  const end = parseLocalDate(endDate);
  if (lang === 'zh') {
    return `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 - ${end.getDate()}日 (纽约时间)`;
  }
  return `${MONTH_SHORT_EN[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()} (ET)`;
}

export default function EventsPreview() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [registerEvent, setRegisterEvent] = useState<FrontendEvent | null>(null);
  const [events, setEvents] = useState<FrontendEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // 加载真实活动数据
  useEffect(() => {
    async function loadEvents() {
      try {
        const data = await getUpcomingEvents();
        setEvents(data);
      } catch (error) {
        console.error('加载活动数据失败:', error);
      } finally {
        setLoading(false);
      }
    }
    loadEvents();
  }, []);

  const isZh = i18n.language === 'zh';
  const upcoming = events.filter((e) => e.status === 'upcoming').slice(0, 3);

  return (
    <section className="py-20 md:py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-4 tracking-widest uppercase">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-event-line" />
              </div>
              {t('events.home.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2">
              {t('events.home.title')}
            </h2>
            <p className="text-[#0A2540]/50 text-base max-w-lg">
              {t('events.home.subtitle')}
            </p>
          </div>
          <Link
            to="/events"
            className="hidden md:inline-flex items-center gap-2 text-[#2C7DA0] text-sm font-medium hover:gap-3 transition-all cursor-pointer whitespace-nowrap shrink-0"
          >
            {t('events.home.viewAll')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>

        {/* Event Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {loading ? (
            <div className="col-span-3 text-center py-12 text-gray-500">
              {isZh ? '加载中...' : 'Loading...'}
            </div>
          ) : upcoming.length === 0 ? (
            <div className="col-span-3 text-center py-12 text-gray-500">
              {isZh ? '暂无即将举办的活动' : 'No upcoming events'}
            </div>
          ) : (
            upcoming.map((event) => (
              <article
                key={event.id}
                className="group bg-white border border-[#0A2540]/10 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-200 flex flex-col"
              >
                {/* Cover */}
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={event.cover}
                    alt={isZh ? event.title : event.titleEn}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  {/* Type Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[event.type]}`}>
                      {t(`event.type.${event.type}`)}
                    </span>
                  </div>
                  {/* Title */}
                  <h3 className="font-bold text-[#0A2540] text-lg mb-2 leading-snug">
                    {isZh ? event.title : event.titleEn}
                  </h3>
                  {/* Meta */}
                  <div className="flex flex-col gap-1 mb-3 text-[#0A2540]/50 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line" />
                      </div>
                      <span>{formatDate(event.date, event.endDate, i18n.language)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      <span>{isZh ? event.location : event.locationEn}</span>
                    </div>
                  </div>
                  {/* Description */}
                  <p className="text-[#0A2540]/60 text-sm leading-relaxed line-clamp-2 mb-4">
                    {isZh ? event.description : event.descriptionEn}
                  </p>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-5">
                    {(isZh ? event.tags : event.tagsEn).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-[#F7F9FC] text-[#0A2540]/50 text-xs rounded-md">
                        {tag}
                      </span>
                    ))}
                  </div>
                  {/* CTA Row */}
                  <div className="mt-auto flex items-center gap-2">
                    {/* 立即报名 Button */}
                    <button
                      onClick={() => setRegisterEvent(event)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#0A2540] hover:bg-[#2C7DA0] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-calendar-check-line" />
                      </div>
                      {isZh ? '立即报名' : 'Register Now'}
                    </button>
                    <Link
                      to="/events"
                      className="flex items-center justify-center gap-1 px-3 py-2.5 border border-[#0A2540]/15 hover:border-[#2C7DA0] text-[#2C7DA0] rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                    >
                      {isZh ? '详情' : 'Details'}
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </Link>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>

        {/* Mobile View All */}
        <div className="md:hidden text-center mt-8">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#0A2540]/20 hover:border-[#2C7DA0] text-[#0A2540] hover:text-[#2C7DA0] rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            {t('events.home.viewAll')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>

      {/* Registration Modal */}
      {registerEvent !== null && (
        <EventDetailModal
          event={registerEvent}
          onClose={() => setRegisterEvent(null)}
          defaultTab="register"
        />
      )}
    </section>
  );
}
